/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { CacheFactoryService } from './factory/cache-factory.service';
import { SseClientCacheFactory } from './factory/sse-client-cache-factory';
import { Logger, MessageService } from '@core/lib/services';
import { Subscription } from 'rxjs';

const log = new Logger('SsePatientImagingHistoryService');

@Injectable({
  providedIn: 'root'
})
export class SsePatientImagingHistoryService {
  private readonly ssePatientHistoryImagingCache: SseClientCacheFactory;
  private comparisonsUpdateEvent = 'comparisonsUpdate';
  private idMap = {} as any;
  private subscribers = [];
  private comparisonErrorSubscriber: Subscription;
  private comparisonCloseSubscriber: Subscription;

  constructor(private cacheFactoryService: CacheFactoryService, private messageService: MessageService) {
    this.ssePatientHistoryImagingCache = this.cacheFactoryService.createSSEClientCacheFactory(
      'ssePatientComparisonsCache',
      this.getEndpointFunction(),
      this.comparator
    );

    this.subscriptions();
  }

  getPatientComparisons(patientId, requestParams, isStreaming, force) {
    this.subscribeEvent(patientId, requestParams.primaryExamId);
    return this.ssePatientHistoryImagingCache.get(patientId, requestParams, isStreaming, force).then(data => {
      return this.cacheFactoryService.idMappingAddHandler(this.idMap, data, patientId, requestParams.primaryExamId);
    });
  }

  removePatientComparisons(requestedProcedureId) {
    this.unsubscribeEvent(requestedProcedureId);
    return this.ssePatientHistoryImagingCache.remove(
      this.cacheFactoryService.idMappingRemoveHandler(this.idMap, requestedProcedureId)
    );
  }

  onComparisonsUpdate(callback, context) {
    return this.ssePatientHistoryImagingCache.on(this.comparisonsUpdateEvent, callback, context);
  }

  getSseClientCacheFactory() {
    return this.ssePatientHistoryImagingCache;
  }

  /************************** Private Functions **************************/

  private subscriptions() {
    this.comparisonErrorSubscriber = this.messageService.on('COMPARISONS.ERROR').subscribe(data => {
      let _primaryExamId = data.requestParameters.primaryExamId[0];
      _primaryExamId = !isFinite(_primaryExamId) ? _primaryExamId.replace('*', '%') : _primaryExamId;
      log.info('Event COMPARISONS.ERROR -> ' + _primaryExamId);
      this.messageService.broadcast('xeErrorResponseDataToController', data);
    });

    this.comparisonCloseSubscriber = this.messageService.on('COMPARISONS.CLOSE').subscribe(data => {
      let _primaryExamId = data.requestParameters.primaryExamId[0];
      _primaryExamId = !isFinite(_primaryExamId) ? _primaryExamId.replace('*', '%') : _primaryExamId;
      log.info('Event COMPARISONS.CLOSE -> ' + _primaryExamId);
      this.messageService.broadcast('streamingClosedDataToController', data);
    });
  }

  private getEndpointFunction() {
    const endpointTemplate = 'patient-history/comparisons?';
    return params => {
      return this.buildURL(params, endpointTemplate);
    };
  }

  private buildURL(params, endpointTemplate) {
    const reqParams = [];
    _.each(params, (val, key) => {
      reqParams.push(key + '=' + val);
    });
    return endpointTemplate + reqParams.join('&');
  }

  private comparator = (patientId, cachedData, serverData) => {
    const cachedDataComparisons = cachedData.patientComparisons;
    const serverDataComparisons = serverData.patientComparisons;
    //Cached and incoming Server data length are same or not.
    let needsUpdate = cachedDataComparisons.length !== serverDataComparisons.length;

    //If both data length are same and both data are from local site
    if (!needsUpdate) {
      if (this.isCacheNeedUpdate(cachedData, serverData)) {
        for (let j = 0; j < cachedDataComparisons.length; j++) {
          const matchedItem = _.find(serverDataComparisons, item => {
            return cachedDataComparisons[j].requestedProcedureId === item.requestedProcedureId;
          });
          //If matched item is 'undefined' that means the data is not there in Cache and need an update.
          needsUpdate = !matchedItem;
          if (needsUpdate) {
            break;
          }
        }
      } else {
        //Requested procedure id will not be used for comparison in case of remote as they are not present in local.
        // These id's are Server generated random id's which will change response by response.
        //If any remote are there in either cached or server, then need an update so negate the 'needsUpdate'.
        needsUpdate = !needsUpdate;
      }
    }
    if (needsUpdate) {
      //Update cachedData with serverData if there is any delta.
      this.ssePatientHistoryImagingCache.updateCache(patientId, serverData);
      this.ssePatientHistoryImagingCache.fireEvent(this.comparisonsUpdateEvent, patientId, serverData, cachedData);
    }
  };

  private isCacheNeedUpdate(cachedData, serverData) {
    if (
      cachedData.requestParameters.primaryExamId[0] === serverData.requestParameters.primaryExamId[0] &&
      cachedData.finalResponseStatus === serverData.finalResponseStatus
    ) {
      // Need Update if Any Site Details Information Updated.
      return (
        _.isEqual(cachedData.siteDetails, serverData.siteDetails) &&
        _.every(cachedData.patientComparisons, item => {
          return item.siteName === 'Local';
        }) &&
        _.every(serverData.patientComparisons, item => {
          return item.siteName === 'Local';
        })
      );
    }
    return false;
  }

  private subscribeEvent(patientId, requestedProcedureId) {
    const subscriber = _.find(this.subscribers, entry => {
      return entry.id === requestedProcedureId;
    });
    if (!subscriber) {
      const mapObject = {
        id: requestedProcedureId,
        instance: this.subscribeMessageEvent(patientId, requestedProcedureId)
      };
      this.subscribers.push(mapObject);
    }
  }

  private unsubscribeEvent(requestedProcedureId) {
    const subscriber = _.find(this.subscribers, entry => {
      return entry.id === requestedProcedureId;
    });
    if (subscriber) {
      subscriber.instance.unsubscribe();
      this.subscribers = _.reject(this.subscribers, entry => {
        return entry.id === requestedProcedureId;
      });
    }
  }

  private subscribeMessageEvent(patientId, requestedProcedureId) {
    const _primaryExamId = !isFinite(requestedProcedureId)
      ? requestedProcedureId.replace('*', '%')
      : requestedProcedureId; //replace * with % for subscribing purpose
    return this.messageService.on('COMPARISONS.MESSAGE.FOR.' + _primaryExamId).subscribe(data => {
      log.info('Event COMPARISONS.MESSAGE.FOR -> ' + data.requestParameters.primaryExamId[0]);
      this.ssePatientHistoryImagingCache.compareAndUpdateCache(patientId, data, this.comparisonsUpdateEvent);
    });
  }

  clearSubscriptions() {
    this.comparisonCloseSubscriber && this.comparisonCloseSubscriber.unsubscribe();
    this.comparisonErrorSubscriber && this.comparisonErrorSubscriber.unsubscribe();
  }
}
